/**
 * Tournament service — read & write.
 *
 * Mutations all funnel through `updateDb` so the in-memory store stays
 * single-sourced and React subscribers re-render. Future prompts will swap
 * the storage layer; the service surface is the seam.
 */

import type {
  CompetitionFormat,
  Id,
  Tournament,
  TournamentSettings,
  TournamentStatus,
  TournamentType,
  Venue,
} from '../models';
import { getDb, updateDb } from './store';
import { newId, nowIso, slugify } from './ids';

export function listTournaments(): Tournament[] {
  return [...getDb().tournaments].sort((a, b) => b.startsAt.localeCompare(a.startsAt));
}

/** List filtered by competition format — 'tournament' or 'season'. */
export function listTournamentsByFormat(format: CompetitionFormat): Tournament[] {
  return listTournaments().filter((t) => (t.format ?? 'tournament') === format);
}

export function listActiveTournaments(): Tournament[] {
  const live: TournamentStatus[] = ['scheduled', 'in_progress', 'paused'];
  return listTournaments().filter((t) => live.includes(t.status));
}

export function getTournament(id: Id): Tournament | undefined {
  return getDb().tournaments.find((t) => t.id === id);
}

export function getTournamentBySlug(slug: string): Tournament | undefined {
  return getDb().tournaments.find((t) => t.slug === slug);
}

export function listTournamentTypes(): TournamentType[] {
  return getDb().tournamentTypes;
}

export function getTournamentType(typeId: string): TournamentType | undefined {
  return getDb().tournamentTypes.find((t) => t.typeId === typeId);
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export interface CreateTournamentInput {
  name: string;
  typeId: string;
  startsAt: string;
  endsAt: string;
  tagline?: string;
  primaryVenueId?: Id;
  /** Tournament block vs recurring season. Defaults to 'tournament'. */
  format?: CompetitionFormat;
  /** Optional override for the seeded settings (rare). */
  settings?: TournamentSettings;
}

export function createTournament(input: CreateTournamentInput): Tournament {
  const id = newId('tour');
  const ts = nowIso();
  const type = getTournamentType(input.typeId);
  const settings =
    input.settings ??
    type?.defaults ??
    (input.format === 'season' ? defaultRollerSeasonSettings() : defaultIronmanSettings());
  const tournament: Tournament = {
    id,
    kind: 'tournament',
    createdAt: ts,
    updatedAt: ts,
    format: input.format ?? 'tournament',
    name: input.name,
    slug: slugify(input.name),
    typeId: input.typeId,
    status: 'draft',
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    primaryVenueId: input.primaryVenueId,
    tagline: input.tagline,
    settings,
  };
  updateDb((db) => ({ ...db, tournaments: [...db.tournaments, tournament] }));
  return tournament;
}

export function updateTournament(
  id: Id,
  patch: Partial<Omit<Tournament, 'id' | 'kind' | 'createdAt'>>,
): Tournament | undefined {
  let updated: Tournament | undefined;
  updateDb((db) => ({
    ...db,
    tournaments: db.tournaments.map((t) => {
      if (t.id !== id) return t;
      updated = {
        ...t,
        ...patch,
        slug: patch.name ? slugify(patch.name) : t.slug,
        updatedAt: nowIso(),
      } as Tournament;
      return updated;
    }),
  }));
  return updated;
}

export function updateTournamentSettings(
  id: Id,
  patch: Partial<TournamentSettings>,
): Tournament | undefined {
  const t = getTournament(id);
  if (!t) return undefined;
  const merged: TournamentSettings = {
    ...t.settings,
    ...patch,
    scoring: { ...t.settings.scoring, ...(patch.scoring ?? {}) },
    scheduling: { ...t.settings.scheduling, ...(patch.scheduling ?? {}) },
    modules: { ...t.settings.modules, ...(patch.modules ?? {}) },
    season: patch.season
      ? {
          ...defaultSeasonSettings(),
          ...(t.settings.season ?? {}),
          ...patch.season,
          registration: {
            ...defaultSeasonRegistrationSettings(),
            ...(t.settings.season?.registration ?? {}),
            ...(patch.season.registration ?? {}),
          },
        }
      : t.settings.season,
    playoffs: patch.playoffs
      ? { ...(t.settings.playoffs ?? defaultPlayoffSettings()), ...patch.playoffs }
      : t.settings.playoffs,
  };
  return updateTournament(id, { settings: merged });
}

export function publishTournament(id: Id): { ok: boolean; missing: string[] } {
  const t = getTournament(id);
  if (!t) return { ok: false, missing: ['tournament not found'] };
  const missing = missingPublishFields(t);
  if (missing.length > 0) return { ok: false, missing };
  updateTournament(id, { status: 'scheduled' });
  return { ok: true, missing: [] };
}

export interface TournamentDeletionImpact {
  tournament: Tournament | undefined;
  divisions: number;
  teams: number;
  rosterEntries: number;
  roleAssignments: number;
  games: number;
  scheduleSlots: number;
  standings: number;
  gameAssignments: number;
  officialAssignments: number;
  gameRosters: number;
  scoreEvents: number;
  gameResults: number;
  communications: number;
  communicationThreads: number;
  communicationThreadMessages: number;
  communicationDeliveries: number;
  announcements: number;
  lockerRoomMessages: number;
  registrations: number;
  paymentIntents: number;
  playerEligibility: number;
  waiverDocuments: number;
  waivers: number;
}

export function canDeleteTournament(tournament: Tournament): boolean {
  return tournament.status === 'draft' || tournament.status === 'setup';
}

export function canArchiveTournament(tournament: Tournament): boolean {
  return (
    tournament.status === 'scheduled' ||
    tournament.status === 'in_progress' ||
    tournament.status === 'paused' ||
    tournament.status === 'completed'
  );
}

export function archiveTournament(id: Id): Tournament | undefined {
  const tournament = getTournament(id);
  if (!tournament) return undefined;
  if (tournament.status === 'archived') return tournament;
  if (!canArchiveTournament(tournament)) {
    throw new Error('Only launched competitions can be archived. Delete draft or setup competitions instead.');
  }
  return updateTournament(id, { status: 'archived' });
}

export function getTournamentDeletionImpact(id: Id): TournamentDeletionImpact {
  const db = getDb();
  const tournament = db.tournaments.find((t) => t.id === id);
  const gameIds = new Set(db.games.filter((g) => g.tournamentId === id).map((g) => g.id));
  const registrations = db.registrations.filter((r) => r.tournamentId === id);
  const registrationIds = new Set(registrations.map((r) => r.id));
  const officialPaymentIntentIds = new Set(
    db.officialAssignments
      .filter((a) => gameIds.has(a.gameId) && a.paymentIntentId)
      .map((a) => a.paymentIntentId as Id),
  );
  const waiverIds = new Set(registrations.flatMap((r) => (r.waiverId ? [r.waiverId] : [])));
  const waiverDocuments = db.waiverDocuments.filter((w) => w.tournamentId === id && !w.isTemplate);
  const waiverDocumentIds = new Set(waiverDocuments.map((w) => w.id));
  const threadIds = new Set(db.communicationThreads.filter((t) => t.tournamentId === id).map((t) => t.id));
  const messageIds = new Set(db.communicationThreadMessages.filter((m) => threadIds.has(m.threadId)).map((m) => m.id));

  return {
    tournament,
    divisions: db.divisions.filter((d) => d.tournamentId === id).length,
    teams: db.teams.filter((t) => t.tournamentId === id).length,
    rosterEntries: db.rosterEntries.filter((r) => r.tournamentId === id).length,
    roleAssignments: db.roleAssignments.filter((a) => a.tournamentId === id).length,
    games: gameIds.size,
    scheduleSlots: db.scheduleSlots.filter((s) => s.tournamentId === id).length,
    standings: db.standings.filter((s) => s.tournamentId === id).length,
    gameAssignments: db.gameAssignments.filter((a) => gameIds.has(a.gameId)).length,
    officialAssignments: db.officialAssignments.filter((a) => gameIds.has(a.gameId)).length,
    gameRosters: db.gameRosters.filter((r) => gameIds.has(r.gameId)).length,
    scoreEvents: db.scoreEvents.filter((e) => gameIds.has(e.gameId)).length,
    gameResults: db.gameResults.filter((r) => gameIds.has(r.gameId)).length,
    communications: db.communications.filter((c) => c.tournamentId === id).length,
    communicationThreads: threadIds.size,
    communicationThreadMessages: messageIds.size,
    communicationDeliveries: db.communicationDeliveries.filter((d) => threadIds.has(d.threadId) || messageIds.has(d.messageId)).length,
    announcements: db.announcements.filter((a) => a.tournamentId === id).length,
    lockerRoomMessages: db.lockerRoomMessages.filter((m) => m.tournamentId === id).length,
    registrations: registrations.length,
    paymentIntents: db.paymentIntents.filter(
      (p) =>
        p.tournamentId === id ||
        (p.registrationId ? registrationIds.has(p.registrationId) : false) ||
        officialPaymentIntentIds.has(p.id),
    ).length,
    playerEligibility: db.playerEligibility.filter((e) => e.tournamentId === id).length,
    waiverDocuments: waiverDocuments.length,
    waivers: db.waivers.filter((w) => waiverIds.has(w.id) || waiverDocumentIds.has(w.documentId)).length,
  };
}

export function deleteTournament(id: Id): void {
  const tournament = getTournament(id);
  if (!tournament) return;
  if (!canDeleteTournament(tournament)) {
    throw new Error('Only draft or setup competitions can be deleted. Archive this competition instead.');
  }

  updateDb((db) => {
    const gameIds = new Set(db.games.filter((g) => g.tournamentId === id).map((g) => g.id));
    const registrations = db.registrations.filter((r) => r.tournamentId === id);
    const registrationIds = new Set(registrations.map((r) => r.id));
    const officialPaymentIntentIds = new Set(
      db.officialAssignments
        .filter((a) => gameIds.has(a.gameId) && a.paymentIntentId)
        .map((a) => a.paymentIntentId as Id),
    );
    const waiverIds = new Set(registrations.flatMap((r) => (r.waiverId ? [r.waiverId] : [])));
    const waiverDocumentIds = new Set(
      db.waiverDocuments
        .filter((w) => w.tournamentId === id && !w.isTemplate)
        .map((w) => w.id),
    );
    const threadIds = new Set(db.communicationThreads.filter((t) => t.tournamentId === id).map((t) => t.id));
    const messageIds = new Set(db.communicationThreadMessages.filter((m) => threadIds.has(m.threadId)).map((m) => m.id));

    return {
      ...db,
      tournaments: db.tournaments.filter((t) => t.id !== id),
      divisions: db.divisions.filter((d) => d.tournamentId !== id),
      teams: db.teams.filter((t) => t.tournamentId !== id),
      rosterEntries: db.rosterEntries.filter((r) => r.tournamentId !== id),
      roleAssignments: db.roleAssignments.filter((a) => a.tournamentId !== id),
      games: db.games.filter((g) => g.tournamentId !== id),
      scheduleSlots: db.scheduleSlots.filter((s) => s.tournamentId !== id),
      standings: db.standings.filter((s) => s.tournamentId !== id),
      gameAssignments: db.gameAssignments.filter((a) => !gameIds.has(a.gameId)),
      officialAssignments: db.officialAssignments.filter((a) => !gameIds.has(a.gameId)),
      gameRosters: db.gameRosters.filter((r) => !gameIds.has(r.gameId)),
      scoreEvents: db.scoreEvents.filter((e) => !gameIds.has(e.gameId)),
      gameResults: db.gameResults.filter((r) => !gameIds.has(r.gameId)),
      communications: db.communications.filter((c) => c.tournamentId !== id),
      communicationThreads: db.communicationThreads.filter((t) => t.tournamentId !== id),
      communicationThreadMessages: db.communicationThreadMessages.filter((m) => !threadIds.has(m.threadId)),
      communicationDeliveries: db.communicationDeliveries.filter((d) => !threadIds.has(d.threadId) && !messageIds.has(d.messageId)),
      announcements: db.announcements.filter((a) => a.tournamentId !== id),
      lockerRoomMessages: db.lockerRoomMessages.filter((m) => m.tournamentId !== id),
      registrations: db.registrations.filter((r) => r.tournamentId !== id),
      paymentIntents: db.paymentIntents.filter(
        (p) =>
          p.tournamentId !== id &&
          !(p.registrationId && registrationIds.has(p.registrationId)) &&
          !officialPaymentIntentIds.has(p.id),
      ),
      playerEligibility: db.playerEligibility.filter((e) => e.tournamentId !== id),
      waiverDocuments: db.waiverDocuments.filter((w) => w.tournamentId !== id || w.isTemplate),
      waivers: db.waivers.filter((w) => !waiverIds.has(w.id) && !waiverDocumentIds.has(w.documentId)),
    };
  });
}

/** Validate the minimum fields required before a draft can be published. */
export function missingPublishFields(t: Tournament): string[] {
  const m: string[] = [];
  if (!t.name?.trim()) m.push('Name');
  if (!t.startsAt) m.push('Start date');
  if (!t.endsAt) m.push('End date');
  if (!t.primaryVenueId) m.push('Primary venue');
  // Need at least one division & one team for the tournament to be runnable.
  const db = getDb();
  const hasDivision = db.divisions.some((d) => d.tournamentId === t.id);
  const hasTeam = db.teams.some((te) => te.tournamentId === t.id);
  const hasRink = t.primaryVenueId ? db.rinks.some((r) => r.venueId === t.primaryVenueId) : false;
  if (t.format === 'season') {
    const season = normalizeSeasonSettings(t.settings.season);
    if (!hasRink) m.push('At least one rink');
    if (season.weekdays.length === 0) m.push('At least one game night');
    if (!season.firstPuckTime) m.push('First puck time');
    if (season.gamesPerRinkPerNight < 1) m.push('Games per rink');
    const registrationConfigured = season.registration.enabled;
    if (!registrationConfigured && !hasDivision) m.push('At least one division or registration intake');
    if (!registrationConfigured && !hasTeam) m.push('At least one team or registration intake');
    if (t.settings.playoffs && t.settings.playoffs.qualifyingTeams < 2) m.push('Valid playoff qualifier count');
  } else {
    if (!hasDivision) m.push('At least one division');
    if (!hasTeam) m.push('At least one team');
  }
  return m;
}

/** Composed status label used by setup overview cards. */
export function setupStatus(t: Tournament): {
  label: string;
  tone: 'info' | 'warn' | 'ok';
  missing: string[];
} {
  if (t.status === 'in_progress') return { label: 'Live', tone: 'ok', missing: [] };
  if (t.status === 'completed') return { label: 'Completed', tone: 'info', missing: [] };
  if (t.status === 'archived') return { label: 'Archived', tone: 'info', missing: [] };
  if (t.status === 'scheduled') return { label: 'Published', tone: 'ok', missing: [] };
  const missing = missingPublishFields(t);
  if (missing.length === 0) return { label: 'Ready to publish', tone: 'ok', missing };
  if (missing.length <= 2) return { label: 'Setup almost done', tone: 'warn', missing };
  return { label: 'Setup incomplete', tone: 'warn', missing };
}

// --------------------------------------------------------------------------
// Venues
// --------------------------------------------------------------------------

export function listVenues(): Venue[] {
  return getDb().venues;
}

export function createVenue(input: Omit<Venue, 'id' | 'createdAt' | 'updatedAt' | 'kind'>): Venue {
  const ts = nowIso();
  const venue: Venue = {
    id: newId('venue'),
    kind: 'venue',
    createdAt: ts,
    updatedAt: ts,
    ...input,
  };
  updateDb((db) => ({ ...db, venues: [...db.venues, venue] }));
  return venue;
}

// --------------------------------------------------------------------------
// Default settings — used when type lookup misses (defensive)
// --------------------------------------------------------------------------

export function defaultIronmanSettings(): TournamentSettings {
  return {
    scoring: {
      trackAssists: true,
      trackPenaltyMinutes: false,
      trackPenaltyShotsOnly: true,
      trackGoalTimestamps: false,
      gameLengthMinutes: 12,
      overtimeFormat: 'straight_shootout',
      shootoutRoundCount: 3,
      pointsRegulationWin: 2,
      pointsOvertimeWin: 2,
      pointsShootoutWin: 2,
      pointsOvertimeLoss: 1,
      pointsShootoutLoss: 1,
      pointsTie: 1,
      tiebreakers: ['wins', 'goal_diff', 'goals_for', 'head_to_head'],
    },
    scheduling: {
      minTeamRestMinutes: 20,
      rinkTurnoverMinutes: 5,
      balanceBackToBacks: true,
    },
    modules: { proShop: false, concessions: false, locker: true, gamification: false },
  };
}

export function defaultSeasonRegistrationSettings(): NonNullable<TournamentSettings['season']>['registration'] {
  return {
    enabled: false,
    intakeType: 'individual',
    feeCents: 5000,
    requireWaiver: true,
    autoApprove: false,
    requiredFields: ['name', 'email'],
    customQuestions: [],
  };
}

export function defaultSeasonSettings(): NonNullable<TournamentSettings['season']> {
  return {
    weekdays: [],
    firstPuckTime: '19:00',
    gamesPerRinkPerNight: 3,
    blackoutDates: [],
    rosterIntake: 'later',
    registration: defaultSeasonRegistrationSettings(),
    halfSplit: false,
  };
}

export function defaultPlayoffSettings(): NonNullable<TournamentSettings['playoffs']> {
  return {
    format: 'single_elim',
    qualifyingTeams: 4,
    seedingSource: 'final_standings',
  };
}

export function defaultRollerSeasonSettings(): TournamentSettings {
  return {
    scoring: {
      periodCount: 3,
      periodLengthMinutes: 12,
      trackAssists: true,
      trackPenaltyMinutes: true,
      trackPenaltyShotsOnly: false,
      trackGoalTimestamps: true,
      gameLengthMinutes: 36,
      overtimeFormat: 'straight_shootout',
      overtimeMinutes: 5,
      overtimeSkaters: 3,
      shootoutRoundCount: 3,
      pointsRegulationWin: 2,
      pointsOvertimeWin: 2,
      pointsShootoutWin: 2,
      pointsOvertimeLoss: 1,
      pointsShootoutLoss: 1,
      pointsTie: 1,
      tiebreakers: ['wins', 'goal_diff', 'goals_for', 'head_to_head', 'fewest_pims'],
    },
    scheduling: {
      minTeamRestMinutes: 45,
      rinkTurnoverMinutes: 10,
      balanceBackToBacks: true,
    },
    modules: { proShop: false, concessions: false, locker: true, gamification: true },
    season: defaultSeasonSettings(),
    playoffs: defaultPlayoffSettings(),
  };
}

export function normalizeSeasonSettings(
  season: TournamentSettings['season'] | undefined,
): NonNullable<TournamentSettings['season']> {
  const registration = season?.registration ?? defaultSeasonRegistrationSettings();
  return {
    ...defaultSeasonSettings(),
    ...(season ?? {}),
    registration: {
      ...defaultSeasonRegistrationSettings(),
      ...registration,
      requiredFields: registration.requiredFields?.length ? registration.requiredFields : ['name', 'email'],
      customQuestions: registration.customQuestions ?? [],
    },
  };
}
